import type { CSSProperties } from "react";
import { useMemo } from "react";
import { CTable, CTableBody, CTableDataCell, CTableHead, CTableHeaderCell, CTableRow } from "@coreui/react-pro";
import type { TradingStrategyAnnualisedReturnDto } from "../../../api/hooks/useTradingStrategies";

const STRATEGY_SERIES = "Strategy";

// Aligned digits without switching font, matching the trades table.
const TABULAR: CSSProperties = { fontVariantNumeric: "tabular-nums" };

interface AnnualisedReturnsTableProps {
  rows: TradingStrategyAnnualisedReturnDto[];
}

interface Period {
  period: string;
  label: string;
}

type Series = [string, Map<string, TradingStrategyAnnualisedReturnDto>];

/**
 * The annualised returns, laid out the way they are read: one column per period, one row per series.
 *
 * Built around several series even though only the strategy's own is published today. The comparison
 * this table exists for is against a benchmark, and a layout assuming a single row would have to be
 * rewritten rather than extended the moment an index arrives. Periods become columns because that is
 * the axis being compared along — a reader scans 1 Yr against 1 Yr.
 *
 * Matches the back office's table so an admin previewing a strategy sees what a customer sees.
 */
export function AnnualisedReturnsTable({ rows }: AnnualisedReturnsTableProps) {
  const { periods, series } = useMemo(() => {
    // Period order comes from displayOrder rather than the label, so "10 Yr" sorts after "3 Yr".
    const periodOrder = new Map<string, { label: string; order: number }>();
    rows.forEach((row) => {
      const seen = periodOrder.get(row.period);
      if (!seen || row.displayOrder < seen.order) {
        periodOrder.set(row.period, { label: row.label, order: row.displayOrder });
      }
    });

    const orderedPeriods = [...periodOrder.entries()]
      .sort((a, b) => a[1].order - b[1].order)
      .map(([period, { label }]) => ({ period, label }));

    const bySeries = new Map<string, Map<string, TradingStrategyAnnualisedReturnDto>>();
    rows.forEach((row) => {
      if (!bySeries.has(row.series)) bySeries.set(row.series, new Map());
      bySeries.get(row.series)!.set(row.period, row);
    });

    // The strategy leads; anything else is a reference and follows it.
    const orderedSeries = [...bySeries.entries()].sort(([a], [b]) => {
      if (a === STRATEGY_SERIES) return -1;
      if (b === STRATEGY_SERIES) return 1;
      return a.localeCompare(b);
    });

    return { periods: orderedPeriods, series: orderedSeries };
  }, [rows]);

  if (rows.length === 0) {
    return (
      <div className="text-body-secondary">
        No annualised returns have been published for this strategy.
      </div>
    );
  }

  // Two layouts rather than one that reflows, because the axes genuinely swap: a period is a column
  // on a desktop and a row on a phone. Chosen in CSS rather than from a measured width — a JS
  // breakpoint hook paints the desktop layout first and reflows once mounted, which on the brochure
  // (WI #271) meant three pages scrolled sideways on first paint at every phone width.
  return (
    <>
      <div className="d-none d-sm-block">
        <PeriodsAcross periods={periods} series={series} />
      </div>
      <div className="d-sm-none">
        <PeriodsDown periods={periods} series={series} />
      </div>
    </>
  );
}

/** The desktop layout: a reader scans 1 Yr against 1 Yr, left to right. */
function PeriodsAcross({ periods, series }: { periods: Period[]; series: Series[] }) {
  return (
    <CTable small responsive className="mb-0">
      <CTableHead>
        <CTableRow>
          <CTableHeaderCell scope="col" />
          {periods.map((period) => (
            <CTableHeaderCell scope="col" key={period.period} className="text-end">
              {period.label}
            </CTableHeaderCell>
          ))}
        </CTableRow>
      </CTableHead>
      <CTableBody>
        {series.map(([seriesName, byPeriod]) => (
          <CTableRow key={seriesName}>
            <CTableHeaderCell scope="row" className="fw-semibold">
              {seriesName}
            </CTableHeaderCell>
            {periods.map((period) => {
              const cell = byPeriod.get(period.period);

              return (
                <CTableDataCell
                  key={period.period}
                  className={cellClass(seriesName, cell)}
                  style={TABULAR}
                >
                  {/* A period longer than the backtest is omitted rather than estimated, so a gap
                      here is the archive saying nothing rather than a value failing to load. */}
                  {cell?.value ?? "—"}
                </CTableDataCell>
              );
            })}
          </CTableRow>
        ))}
      </CTableBody>
    </CTable>
  );
}

/**
 * The phone layout: the same table transposed, so the figures run down the screen.
 *
 * Transposed rather than collapsed into a card per period, because the comparison is what the table
 * is for: a benchmark stays in the next column on the same row, where it can still be read against
 * the strategy at a glance. Series count stays small — a strategy and something to judge it against
 * — so the columns fit the 360px floor where five periods never could.
 */
function PeriodsDown({ periods, series }: { periods: Period[]; series: Series[] }) {
  return (
    <CTable small className="mb-0">
      <CTableHead>
        <CTableRow>
          <CTableHeaderCell scope="col" />
          {series.map(([seriesName]) => (
            <CTableHeaderCell scope="col" key={seriesName} className="text-end">
              {seriesName}
            </CTableHeaderCell>
          ))}
        </CTableRow>
      </CTableHead>
      <CTableBody>
        {periods.map((period) => (
          <CTableRow key={period.period}>
            <CTableHeaderCell scope="row" className="fw-semibold">
              {period.label}
            </CTableHeaderCell>
            {series.map(([seriesName, byPeriod]) => {
              const cell = byPeriod.get(period.period);

              return (
                <CTableDataCell
                  key={seriesName}
                  className={cellClass(seriesName, cell)}
                  style={TABULAR}
                >
                  {cell?.value ?? "—"}
                </CTableDataCell>
              );
            })}
          </CTableRow>
        ))}
      </CTableBody>
    </CTable>
  );
}

/**
 * The strategy's own row is coloured by sign; a benchmark stays muted.
 *
 * The benchmark is the reference rather than the subject, and colouring both would make the table
 * read as two competing claims instead of one figure and something to judge it against.
 */
function cellClass(
  seriesName: string,
  cell: TradingStrategyAnnualisedReturnDto | undefined
): string {
  const base = "text-end";
  if (!cell) return `${base} text-body-secondary`;
  if (seriesName !== STRATEGY_SERIES) return `${base} text-body-secondary`;

  // Falls back to neutral where no figure was published — the text still reads, and inventing a
  // sign from a missing number would be a guess.
  if (cell.numericValue == null) return base;

  return `${base} ${cell.numericValue < 0 ? "text-danger" : "text-success"}`;
}
